import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight, CornerDownLeft, Menu, Moon, Search, Sun, X } from 'lucide-react';
import { DOCS_BASE, SECCIONES_DOCS, vecinas } from '../content/registry';
import { buscarEnDocs } from '../search/buscador';
import type { DocsTema } from '../useDocsTheme';

/**
 * El marco del centro de ayuda: barra, menu, migas, tabla de contenidos,
 * buscador y el pie de anterior/siguiente.
 *
 * Todo sale del registro de paginas, asi que no hay forma de que una pagina
 * exista y no aparezca en el menu, ni de que aparezca y no exista.
 */

/* --------------------------------------------------------------- barra --- */

export function DocsHeader({
  tema,
  onAlternarTema,
  onBuscar,
  onMenu,
}: {
  tema: DocsTema;
  onAlternarTema: () => void;
  onBuscar: () => void;
  onMenu: () => void;
}): JSX.Element {
  return (
    <header
      className="sticky top-0 z-30 border-b backdrop-blur"
      style={{
        borderColor: 'var(--dk-borde)',
        background: 'color-mix(in srgb, var(--dk-fondo) 88%, transparent)',
      }}
    >
      <div className="mx-auto flex h-14 max-w-[1440px] items-center gap-3 px-3 sm:px-5">
        <button
          type="button"
          onClick={onMenu}
          aria-label="Abrir el menu de la ayuda"
          className="rounded-lg p-2 lg:hidden"
          style={{ color: 'var(--dk-texto-suave)' }}
        >
          <Menu size={19} />
        </button>

        <Link to={DOCS_BASE} className="flex shrink-0 items-center gap-2.5">
          {/*
            El isotipo de ED Ecosystem, el mismo que en el portal: entrar en la
            ayuda no deberia parecer que te ha sacado a otro producto.

            Va sobre un circulo claro porque la ayuda tiene modo oscuro y el
            isotipo es negro y rojo: sin fondo, en oscuro desaparece la mitad
            negra y lo que queda parece una coma roja. El circulo es del mismo
            blanco en los dos temas a proposito —es parte del logotipo, no de
            la interfaz— y en claro apenas se nota.
          */}
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
            style={{ background: '#ffffff' }}
          >
            <img
              src="/ed-ecosystem-logo.png"
              alt=""
              aria-hidden
              width={20}
              height={20}
              className="h-5 w-5 object-contain"
            />
          </span>
          <span className="text-[13px] font-semibold" style={{ color: 'var(--dk-texto)' }}>
            Centro de ayuda
          </span>
        </Link>

        {/*
          El campo de busqueda es un BOTON con aspecto de campo, no un `input`.
          Al pulsarlo se abre el dialogo, que es donde se escribe de verdad. Un
          campo real aqui obligaria a mantener dos cajas sincronizadas y, peor,
          dejaria escribir en un sitio que no ensena resultados.
        */}
        <button
          type="button"
          onClick={onBuscar}
          className="ml-auto flex h-9 items-center gap-2 rounded-lg border px-3 text-[13px] sm:ml-6 sm:w-full sm:max-w-sm"
          style={{
            borderColor: 'var(--dk-borde)',
            background: 'var(--dk-fondo-sutil)',
            color: 'var(--dk-texto-tenue)',
          }}
        >
          <Search size={15} aria-hidden />
          <span className="hidden sm:inline">Buscar en la ayuda…</span>
          <span
            className="ml-auto hidden shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-semibold sm:inline"
            style={{ borderColor: 'var(--dk-borde-fuerte)' }}
          >
            Ctrl K
          </span>
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:ml-3">
          <button
            type="button"
            onClick={onAlternarTema}
            aria-label={tema === 'oscuro' ? 'Usar tema claro' : 'Usar tema oscuro'}
            title={tema === 'oscuro' ? 'Usar tema claro' : 'Usar tema oscuro'}
            className="rounded-lg border p-2"
            style={{ borderColor: 'var(--dk-borde)', color: 'var(--dk-texto-suave)' }}
          >
            {tema === 'oscuro' ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/*
            Enlace normal y no `<Link>` a proposito: se sale de la ayuda hacia
            el portal, que exige sesion y monta su propio arbol. Ademas asi
            funciona igual para quien llego a /docs desde fuera.
          */}
          <a
            href="/"
            className="hidden items-center gap-1.5 rounded-lg px-3 py-2 text-[13px] font-medium sm:inline-flex"
            style={{ background: 'var(--dk-acento-boton)', color: '#fff' }}
          >
            Ir al portal
          </a>
        </div>
      </div>
    </header>
  );
}

/* ---------------------------------------------------------------- menu --- */

/**
 * Menu lateral.
 *
 * Todas las secciones van abiertas a proposito. Plegarlas ahorraria unos
 * centimetros de barra y a cambio esconderia media ayuda detras de un clic,
 * que es justo lo contrario de lo que debe hacer un indice.
 */
export function DocsSidebar({ alNavegar }: { alNavegar?: () => void }): JSX.Element {
  return (
    <nav aria-label="Secciones de la ayuda" className="pb-10">
      {SECCIONES_DOCS.map((seccion) => (
        <div key={seccion.id} className="mb-6">
          <p
            className="mb-2 flex items-center gap-2 px-3 text-[12.5px] font-bold uppercase tracking-wide"
            style={{ color: 'var(--dk-texto)' }}
          >
            <span
              aria-hidden
              className="h-3 w-[3px] shrink-0 rounded-full"
              style={{ background: 'var(--dk-acento)' }}
            />
            {seccion.titulo}
          </p>
          <ul>
            {seccion.paginas.map((pagina) => (
              <li key={pagina.slug}>
                <NavLink
                  to={pagina.ruta}
                  onClick={alNavegar}
                  className="block rounded-lg px-3 py-1.5 text-[13.5px] leading-snug"
                  style={({ isActive }) => ({
                    background: isActive ? 'var(--dk-acento-suave)' : 'transparent',
                    color: isActive ? 'var(--dk-acento-texto)' : 'var(--dk-texto-suave)',
                    fontWeight: isActive ? 600 : 400,
                  })}
                >
                  {pagina.titulo}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/* ---------------------------------------------------------------- migas --- */

/**
 * Migas de pan: Ayuda › Seccion › Pagina.
 *
 * La seccion no es un enlace porque no hay una pagina de seccion a la que ir.
 * Pintarla como enlace prometeria un destino que no existe.
 */
export function DocsBreadcrumbs({
  seccion,
  titulo,
}: {
  seccion?: string;
  titulo: string;
}): JSX.Element {
  return (
    <nav aria-label="Ruta de navegacion" className="mb-3">
      <ol className="flex flex-wrap items-center gap-1 text-[12.5px]">
        <li>
          <Link to={DOCS_BASE} className="hover:underline" style={{ color: 'var(--dk-texto-tenue)' }}>
            Ayuda
          </Link>
        </li>
        {seccion ? (
          <li className="flex items-center gap-1">
            <ChevronRight size={13} style={{ color: 'var(--dk-borde-fuerte)' }} aria-hidden />
            <span style={{ color: 'var(--dk-texto-tenue)' }}>{seccion}</span>
          </li>
        ) : null}
        <li className="flex items-center gap-1">
          <ChevronRight size={13} style={{ color: 'var(--dk-borde-fuerte)' }} aria-hidden />
          <span aria-current="page" className="font-medium" style={{ color: 'var(--dk-texto)' }}>
            {titulo}
          </span>
        </li>
      </ol>
    </nav>
  );
}

/* --------------------------------------------------- anterior/siguiente --- */

/**
 * Llevan el titulo de destino, no solo la flecha: quien esta decidiendo si
 * seguir leyendo necesita saber hacia donde, y «Siguiente →» no lo dice.
 */
export function DocsPrevNext({ slug }: { slug: string }): JSX.Element | null {
  const { anterior, siguiente } = vecinas(slug);
  if (!anterior && !siguiente) return null;

  return (
    <nav
      aria-label="Paginas contiguas"
      className="mt-12 grid gap-3 border-t pt-6 sm:grid-cols-2"
      style={{ borderColor: 'var(--dk-borde)' }}
    >
      {anterior ? (
        <EnlaceVecina titulo={anterior.titulo} ruta={anterior.ruta} esAnterior />
      ) : (
        <span aria-hidden />
      )}
      {siguiente ? <EnlaceVecina titulo={siguiente.titulo} ruta={siguiente.ruta} /> : null}
    </nav>
  );
}

function EnlaceVecina({
  titulo,
  ruta,
  esAnterior = false,
}: {
  titulo: string;
  ruta: string;
  esAnterior?: boolean;
}): JSX.Element {
  const Icono = esAnterior ? ArrowLeft : ArrowRight;

  return (
    <Link
      to={ruta}
      rel={esAnterior ? 'prev' : 'next'}
      className={`group flex flex-col rounded-xl border px-4 py-3 transition-shadow hover:shadow-md ${
        esAnterior ? 'items-start' : 'items-end sm:col-start-2'
      }`}
      style={{ borderColor: 'var(--dk-borde)', background: 'var(--dk-fondo-elevado)' }}
    >
      <span
        className="flex items-center gap-1 text-[11.5px] uppercase tracking-wider"
        style={{ color: 'var(--dk-texto-tenue)' }}
      >
        {esAnterior ? <Icono size={12} /> : null}
        {esAnterior ? 'Anterior' : 'Siguiente'}
        {esAnterior ? null : <Icono size={12} />}
      </span>
      <span
        className={`mt-0.5 text-[14px] font-semibold ${esAnterior ? 'text-left' : 'text-right'}`}
        style={{ color: 'var(--dk-acento-texto)' }}
      >
        {titulo}
      </span>
    </Link>
  );
}

/* ------------------------------------------------- tabla de contenidos --- */

interface ApartadoVisible {
  id: string;
  titulo: string;
  nivel: number;
}

/**
 * Tabla de contenidos de la pagina.
 *
 * Lee los apartados del DOM en vez de recibirlos en una lista: la pagina ya
 * dice cuales tiene al pintarlos, y una segunda lista escrita a mano acabaria
 * ofreciendo saltos a apartados que se movieron o se quitaron.
 */
export function DocsToc({
  contenedor,
  slug,
}: {
  contenedor: RefObject<HTMLElement>;
  slug: string;
}): JSX.Element | null {
  const [apartados, setApartados] = useState<ApartadoVisible[]>([]);
  const [activo, setActivo] = useState<string | null>(null);

  useEffect(() => {
    const raiz = contenedor.current;
    if (!raiz) return undefined;

    const leer = (): void => {
      const encontrados = [...raiz.querySelectorAll<HTMLElement>('[data-dk-seccion]')].map((el) => ({
        id: el.dataset.dkSeccion ?? '',
        titulo: (el.textContent ?? '').replace(/\s+$/, ''),
        nivel: Number(el.dataset.dkNivel ?? 2),
      }));
      setApartados((previos) => {
        const igual =
          previos.length === encontrados.length &&
          previos.every((p, i) => p.id === encontrados[i].id);
        return igual ? previos : encontrados;
      });
    };

    leer();
    // Las paginas se cargan aparte y aparecen despues; sin vigilancia, la
    // tabla se quedaria con los apartados que hubiera en el primer instante.
    const observador = new MutationObserver(leer);
    observador.observe(raiz, { childList: true, subtree: true });
    return () => observador.disconnect();
  }, [contenedor, slug]);

  /*
   * Marcar el apartado en el que se esta.
   *
   * El margen inferior de -70% hace que cuente el apartado cuyo titulo esta en
   * la franja ALTA de la pantalla, que es el que se esta leyendo. Sin el, al
   * llegar al final se marcaria el ultimo apartado visible aunque su titulo
   * estuviera fuera de cuadro.
   */
  useEffect(() => {
    if (!apartados.length) return undefined;

    const observador = new IntersectionObserver(
      (entradas) => {
        const visibles = entradas
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const primero = visibles[0]?.target as HTMLElement | undefined;
        if (primero?.dataset.dkSeccion) setActivo(primero.dataset.dkSeccion);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );

    for (const apartado of apartados) {
      const el = document.getElementById(apartado.id);
      if (el) observador.observe(el);
    }
    return () => observador.disconnect();
  }, [apartados]);

  if (apartados.length < 2) return null;

  return (
    <nav aria-label="En esta pagina" className="text-[13px]">
      <p
        className="mb-3 text-[11px] font-semibold uppercase tracking-wider"
        style={{ color: 'var(--dk-texto-tenue)' }}
      >
        En esta pagina
      </p>
      <ul className="space-y-1 border-l" style={{ borderColor: 'var(--dk-borde)' }}>
        {apartados.map((apartado) => {
          const esActivo = activo === apartado.id;
          return (
            <li key={apartado.id}>
              <a
                href={`#${apartado.id}`}
                aria-current={esActivo ? 'location' : undefined}
                className={`-ml-px block border-l-2 py-1 leading-snug ${
                  apartado.nivel === 3 ? 'pl-6' : 'pl-3'
                }`}
                style={{
                  borderColor: esActivo ? 'var(--dk-acento)' : 'transparent',
                  color: esActivo ? 'var(--dk-acento-texto)' : 'var(--dk-texto-tenue)',
                  fontWeight: esActivo ? 600 : 400,
                }}
              >
                {apartado.titulo}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* ------------------------------------------------------------ buscador --- */

/**
 * Busqueda de la ayuda. Se abre con Ctrl/Cmd + K o pulsando el campo.
 *
 * Las flechas mueven la seleccion sin sacar el foco del campo. Quien busca
 * esta escribiendo: si para bajar por los resultados hubiera que tabular,
 * habria que volver al campo para corregir una letra.
 */
export function DocsSearch({
  abierto,
  onCerrar,
}: {
  abierto: boolean;
  onCerrar: () => void;
}): JSX.Element | null {
  const navigate = useNavigate();
  const [consulta, setConsulta] = useState('');
  const [indiceActivo, setIndiceActivo] = useState(0);
  const campo = useRef<HTMLInputElement>(null);
  const lista = useRef<HTMLDivElement>(null);

  const resultados = useMemo(() => buscarEnDocs(consulta), [consulta]);

  // Al abrir se limpia y se enfoca: una busqueda anterior a medio escribir es
  // ruido, no un punto de partida.
  useEffect(() => {
    if (!abierto) return undefined;
    setConsulta('');
    setIndiceActivo(0);
    const t = window.setTimeout(() => campo.current?.focus(), 10);
    return () => window.clearTimeout(t);
  }, [abierto]);

  useEffect(() => setIndiceActivo(0), [consulta]);

  useEffect(() => {
    lista.current?.querySelector('[data-activo="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [indiceActivo]);

  if (!abierto) return null;

  const ir = (ruta: string | undefined): void => {
    if (!ruta) return;
    navigate(ruta);
    onCerrar();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]"
      style={{ background: 'rgba(15,23,42,0.45)' }}
      onMouseDown={(evento) => {
        // Solo cierra el clic en el propio fondo. Sin la comprobacion, soltar
        // el raton fuera tras seleccionar texto dentro cerraria el dialogo.
        if (evento.target === evento.currentTarget) onCerrar();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Buscar en la ayuda"
        className="w-full max-w-xl overflow-hidden rounded-2xl border shadow-2xl"
        style={{ background: 'var(--dk-fondo-elevado)', borderColor: 'var(--dk-borde)' }}
      >
        <div className="flex items-center gap-2.5 border-b px-4" style={{ borderColor: 'var(--dk-borde)' }}>
          <Search size={17} style={{ color: 'var(--dk-texto-tenue)' }} aria-hidden />
          <input
            ref={campo}
            type="text"
            value={consulta}
            onChange={(e) => setConsulta(e.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Escape') {
                evento.preventDefault();
                onCerrar();
              } else if (evento.key === 'ArrowDown') {
                evento.preventDefault();
                setIndiceActivo((i) => (resultados.length ? (i + 1) % resultados.length : 0));
              } else if (evento.key === 'ArrowUp') {
                evento.preventDefault();
                setIndiceActivo((i) =>
                  resultados.length ? (i - 1 + resultados.length) % resultados.length : 0,
                );
              } else if (evento.key === 'Enter') {
                evento.preventDefault();
                ir(resultados[indiceActivo]?.ruta);
              }
            }}
            placeholder="Buscar en la ayuda…"
            aria-label="Buscar en la ayuda"
            autoComplete="off"
            className="w-full bg-transparent py-3.5 text-[15px] outline-none"
            style={{ color: 'var(--dk-texto)' }}
          />
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar busqueda"
            className="rounded p-1"
            style={{ color: 'var(--dk-texto-tenue)' }}
          >
            <X size={16} />
          </button>
        </div>

        <div ref={lista} className="max-h-[52vh] overflow-y-auto p-2">
          {!consulta ? (
            <p className="px-3 py-6 text-center text-[13px]" style={{ color: 'var(--dk-texto-tenue)' }}>
              Escribe para buscar. Por ejemplo: <em>ticket</em>, <em>accesos</em> o{' '}
              <em>contrasena</em>.
            </p>
          ) : null}

          {consulta && !resultados.length ? (
            <p className="px-3 py-6 text-center text-[13px]" style={{ color: 'var(--dk-texto-tenue)' }}>
              Sin resultados para «{consulta}».
            </p>
          ) : null}

          <ul>
            {resultados.map((resultado, i) => (
              <li key={resultado.slug}>
                <button
                  type="button"
                  data-activo={i === indiceActivo}
                  onMouseEnter={() => setIndiceActivo(i)}
                  onClick={() => ir(resultado.ruta)}
                  className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left"
                  style={{
                    background: i === indiceActivo ? 'var(--dk-acento-suave)' : 'transparent',
                  }}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-semibold" style={{ color: 'var(--dk-texto)' }}>
                      {resultado.titulo}
                    </span>
                    <span
                      className="mt-0.5 block truncate text-[12.5px]"
                      style={{ color: 'var(--dk-texto-tenue)' }}
                    >
                      {resultado.seccion} · {resultado.descripcion}
                    </span>
                  </span>
                  {i === indiceActivo ? (
                    <CornerDownLeft
                      size={14}
                      className="mt-1 shrink-0"
                      style={{ color: 'var(--dk-texto-tenue)' }}
                      aria-hidden
                    />
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
